import { Box, Stack, Text, Icon, Center, Button } from '@chakra-ui/react';
import {
  MdCheckCircle,
  MdFavorite,
  MdBookmark,
  MdNotifications,
  MdCalendarMonth,
  MdGavel,
} from 'react-icons/md';
import {
  UserAction,
  NestedHome,
  NestedProject,
  NestedUser,
} from '@habity/types';
import { getStreet, getHomePath } from '@habity/services';
import { ICONS } from '~/constants';
import { ROUTES } from '@habity/constants';
import { useRouter } from 'next/navigation';
import { Link } from '@chakra-ui/next-js';

interface ActionConfirmationProps {
  action: UserAction;
  home?: NestedHome;
  project?: NestedProject;
  onClose: () => void;
  email: string;
  homeStatus?: string;
  valuation?: number | null;
  /** The logged-in user (for showing dashboard buttons and hiding magic link message) */
  user?: NestedUser | null;
}

interface ActionConfig {
  icon: any;
  title: string;
  message: string;
  buttonText: string;
  showHomeAddress?: boolean;
  showMagicLinkMessage?: boolean;
  showValuationOption?: boolean;
}

const getActionConfig = (
  action: UserAction,
  valuation?: number | null,
  isProject?: boolean
): ActionConfig => {
  switch (action) {
    case 'liked':
      return {
        icon: MdFavorite,
        title: isProject ? 'Projektet är gillat!' : 'Bostaden är gillad!',
        message: isProject
          ? 'Du har gillat detta projekt. Du kan se alla dina gillade projekt i din profil.'
          : 'Du har gillat denna bostad. Du kan se alla dina favoriter i din profil.',
        buttonText: 'Stäng',
        showHomeAddress: true,
        showMagicLinkMessage: true,
        showValuationOption: !isProject,
      };
    case 'added_to_wishlist':
      return {
        icon: MdBookmark,
        title: isProject ? 'Projektet är sparat!' : 'Bostaden är sparad!',
        message: isProject
          ? 'Du har sparat detta projekt i din bevakningslista. Vi meddelar dig om det händer något nytt.'
          : 'Du har sparat denna bostad i din bevakningslista. Vi meddelar dig om det händer något nytt.',
        buttonText: 'Stäng',
        showHomeAddress: true,
        showMagicLinkMessage: true,
        showValuationOption: !isProject,
      };
    case 'clicked_public_preview_home':
      return {
        icon: MdNotifications,
        title: 'Välkommen in!',
        message:
          'Du är nu registrerad som intressent för denna underhandsbostad. Mäklaren kommer att kontakta dig med mer information.',
        buttonText: 'Visa bostaden',
        showHomeAddress: true,
        showMagicLinkMessage: true,
        showValuationOption: false,
      };
    case 'showed_interest':
      return {
        icon: MdNotifications,
        title: 'Intresse anmält!',
        message: isProject
          ? 'Utvecklaren har fått din intresseanmälan och kommer att kontakta dig.'
          : 'Mäklaren har fått din intresseanmälan och kommer att kontakta dig.',
        buttonText: 'Stäng',
        showHomeAddress: true,
        showMagicLinkMessage: true,
        showValuationOption: !isProject,
      };
    case 'booked_viewing':
      return {
        icon: MdCalendarMonth,
        title: 'Visning bokad!',
        message:
          'Din visning är bokad. Du får en bekräftelse via e-post med alla detaljer.',
        buttonText: 'Stäng',
        showHomeAddress: true,
        showMagicLinkMessage: true,
        showValuationOption: !isProject,
      };
    case 'followed_bidding':
      return {
        icon: MdGavel,
        title: isProject ? 'Du följer projektet!' : 'Du följer budgivningen!',
        message: isProject
          ? 'Vi meddelar dig när det kommer nyheter om detta projekt.'
          : 'Vi meddelar dig när det kommer nya bud på denna bostad.',
        buttonText: 'Stäng',
        showHomeAddress: true,
        showMagicLinkMessage: true,
        showValuationOption: !isProject,
      };
    // Seller events (not intents)
    case 'claimed_home':
      return {
        icon: MdCheckCircle,
        title: 'Bostaden är anmäld!',
        message:
          'Vi har tagit emot din anmälan och kommer att kontakta dig inom kort.',
        buttonText: 'Stäng',
        showHomeAddress: true,
        showMagicLinkMessage: true,
        showValuationOption: false,
      };
    case 'booked_broker_valuation':
      return {
        icon: MdCheckCircle,
        title: 'Värdering bokad!',
        message:
          'Din värdering är bokad. Du får en bekräftelse via e-post med alla detaljer.',
        buttonText: 'Stäng',
        showHomeAddress: true,
        showMagicLinkMessage: true,
        showValuationOption: false,
      };
    case 'requested_broker_valuation':
      return {
        icon: MdCheckCircle,
        title: valuation
          ? 'Din värdeindikation är klar!'
          : 'Tack för din värderingsförfrågan!',
        message: valuation
          ? 'Här är din preliminära värdering baserad på aktuella marknadsdata.'
          : 'Vi har tagit emot din förfrågan om värdering och kommer att kontakta dig inom kort.',
        buttonText: 'Stäng',
        showHomeAddress: true,
        showMagicLinkMessage: true,
        showValuationOption: false,
      };
    case 'booked_meeting':
      return {
        icon: MdCheckCircle,
        title: 'Möte bokat!',
        message:
          'Ditt möte är bokat. Du får en bekräftelse via e-post med alla detaljer.',
        buttonText: 'Stäng',
        showHomeAddress: true,
        showMagicLinkMessage: true,
        showValuationOption: false,
      };
    case 'requested_meeting':
      return {
        icon: MdCheckCircle,
        title: 'Tack för din mötesförfrågan!',
        message:
          'Vi har tagit emot din förfrågan och kommer att kontakta dig inom kort för att boka in ett möte.',
        buttonText: 'Stäng',
        showHomeAddress: true,
        showMagicLinkMessage: true,
        showValuationOption: false,
      };
    case 'created_search_profile':
      return {
        icon: MdNotifications,
        title: 'Bevakning skapad!',
        message:
          'Din sökning är nu aktiv. Du får e-post när nya bostäder matchar dina kriterier.',
        buttonText: 'Stäng',
        showHomeAddress: false,
        showMagicLinkMessage: true,
        showValuationOption: false,
      };
    case 'subscribed_final_price':
      return {
        icon: MdNotifications,
        title: 'Slutprisbevakning aktiverad!',
        message:
          'Du får ett meddelande när slutpriset för denna bostad publiceras.',
        buttonText: 'Stäng',
        showHomeAddress: true,
        showMagicLinkMessage: true,
        showValuationOption: false,
      };
    default:
      return {
        icon: MdCheckCircle,
        title: 'Klart!',
        message: 'Din åtgärd har genomförts.',
        buttonText: 'Stäng',
        showHomeAddress: true,
        showMagicLinkMessage: true,
        showValuationOption: false,
      };
  }
};

const getStatusActionConfig = (
  action: UserAction,
  status: string
): ActionConfig | null => {
  // Status-specific overrides for actions
  const statusConfigs: Record<
    string,
    Partial<Record<UserAction, ActionConfig>>
  > = {
    premarket: {
      clicked_public_preview_home: {
        icon: ICONS.MAIL,
        title: 'Välkommen in!',
        message:
          'Du har nu exklusiv tillgång till denna underhandsbostad. Klicka nedan för att se bostaden eller använd länken i din inkorg.',
        buttonText: 'Visa bostaden',
        showHomeAddress: false,
        showMagicLinkMessage: false,
      },
    },
  };

  return statusConfigs[status]?.[action] || null;
};

export const ActionConfirmation = ({
  action,
  home,
  project,
  onClose,
  email,
  homeStatus,
  valuation,
  user,
}: ActionConfirmationProps) => {
  const router = useRouter();

  const isProject = !!project;
  const isLoggedIn = !!user;

  // Try to get status-specific config first, fall back to default action config
  const statusConfig = homeStatus
    ? getStatusActionConfig(action, homeStatus)
    : null;
  const baseConfig =
    statusConfig || getActionConfig(action, valuation, isProject);

  // Override showMagicLinkMessage to false if user is logged in
  const config = {
    ...baseConfig,
    showMagicLinkMessage: isLoggedIn ? false : baseConfig.showMagicLinkMessage,
  };

  return (
    <Box py={{ base: '8', sm: '8' }} px={{ base: '10', sm: '10' }}>
      <Stack spacing="8" align="center" textAlign="center">
        <Center bg="gray.100" w="80px" h="80px" borderRadius="full">
          <Icon as={config.icon} boxSize="40px" color="blackAlpha.900" />
        </Center>

        <Stack spacing="4">
          <Text fontSize="xl" fontWeight="bold">
            {config.title}
          </Text>
          <Text color="gray.600">{config.message}</Text>

          {valuation && action === 'requested_broker_valuation' && (
            <Box
              bg="brand.50"
              borderRadius="lg"
              p={6}
              mt={4}
              borderWidth="2px"
              borderColor="brand.200"
            >
              <Text fontSize="sm" color="gray.600" mb={2}>
                Uppskattat värde
              </Text>
              <Text fontSize="3xl" fontWeight="bold" color="brand.600">
                {new Intl.NumberFormat('sv-SE', {
                  style: 'currency',
                  currency: 'SEK',
                  maximumFractionDigits: 0,
                }).format(valuation)}
              </Text>
              <Text fontSize="xs" color="gray.500" mt={2}>
                Mäklaren kommer att kontakta dig för att diskutera mer exakt
                värdering.
              </Text>
            </Box>
          )}

          {config.showHomeAddress && (
            <Text fontWeight="medium" mt={2}>
              {project
                ? project.name || project.heading || project.city || 'Projektet'
                : home
                ? getStreet(home)
                : null}
            </Text>
          )}

          {config.showMagicLinkMessage && (
            <Text color="gray.600" fontSize="xs">
              Vi har även skickat en inloggningslänk till{' '}
              <Text as="span" fontSize="xs" fontWeight="bold">
                {email}
              </Text>{' '}
              som du kan klicka på för att fortsätta använda sajten i inloggat
              läge.
            </Text>
          )}
        </Stack>

        {action === 'created_search_profile' && isLoggedIn && user ? (
          <Stack width="full" spacing="3">
            <Button
              as={Link}
              href={`${ROUTES.DASHBOARD.BUYER.INDEX}?tab=searches`}
              width="full"
              bg="blackAlpha.900"
              color="white"
              _hover={{ bg: 'blackAlpha.700', textDecoration: 'none' }}
              size="lg"
            >
              Visa bevakningar ({user?.searches?.length ?? 0})
            </Button>
            <Button onClick={onClose} width="full" variant="outline" size="lg">
              {config.buttonText}
            </Button>
          </Stack>
        ) : (action === 'liked' || action === 'added_to_wishlist') &&
          isLoggedIn &&
          user ? (
          <Stack width="full" spacing="3">
            <Button
              as={Link}
              href={`${ROUTES.DASHBOARD.BUYER.INDEX}?tab=wishlists`}
              width="full"
              bg="blackAlpha.900"
              color="white"
              _hover={{ bg: 'blackAlpha.700', textDecoration: 'none' }}
              size="lg"
            >
              Visa favoriter (
              {user?.interests?.filter(
                (i) => i.interest_level === 'added_to_wishlist'
              ).length ?? 0}
              )
            </Button>
            <Button onClick={onClose} width="full" variant="outline" size="lg">
              {config.buttonText}
            </Button>
          </Stack>
        ) : action === 'showed_interest' && isLoggedIn && user ? (
          <Stack width="full" spacing="3">
            <Button
              as={Link}
              href={`${ROUTES.DASHBOARD.BUYER.INDEX}?tab=interests`}
              width="full"
              bg="blackAlpha.900"
              color="white"
              _hover={{ bg: 'blackAlpha.700', textDecoration: 'none' }}
              size="lg"
            >
              Visa intresseanmälda (
              {user?.interests?.filter(
                (i) => i.interest_level === 'showed_interest'
              ).length ?? 0}
              )
            </Button>
            <Button onClick={onClose} width="full" variant="outline" size="lg">
              {config.buttonText}
            </Button>
          </Stack>
        ) : (
          <Button
            onClick={() => {
              // For premarket buyers, allow direct navigation to the home when clicking the primary button
              if (homeStatus === 'premarket' && home) {
                const path = getHomePath(home);
                if (path) {
                  router.push(path);
                }
              }
              onClose();
            }}
            width="full"
            bg="blackAlpha.900"
            color="white"
            _hover={{ bg: 'blackAlpha.700' }}
            size="lg"
          >
            {config.buttonText}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
